/**
 * Foundation Option Picker
 * Handles switching between Bundle (Bed Frame & Headboard) and Single (Bed Frame Only) products
 * on the same page without redirection. Uses the same dropdown structure as product-variant-size-dropdown.liquid.
 */
(function () {
  "use strict";

  function initFoundationOptionPicker() {
    const picker = document.querySelector(".foundation-option-picker");
    if (!picker) return;

    const sectionId =
      picker.closest('[id^="MainProduct-"]')?.id?.replace("MainProduct-", "") ||
      "";
    const dataScript = document.getElementById(
      `foundation-picker-data-${sectionId}`
    );

    if (!dataScript) {
      console.warn("Foundation picker data not found");
      return;
    }

    let pickerData;
    try {
      pickerData = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error("Failed to parse foundation picker data:", e);
      return;
    }

    const optionButtons = picker.querySelectorAll(
      "._1_pp_mat_btn[data-option]"
    );

    // Get dropdown elements using the same ID pattern as product-variant-size-dropdown.liquid
    const selectId = `foundation-size-select-${sectionId}`;
    const dropdownId = `foundation-size-dropdown-${sectionId}`;
    const optionsId = `foundation-size-dropdown-options-${sectionId}`;

    // Determine current option based on page product or default to bundle
    let currentOption =
      pickerData.currentProduct === pickerData.single?.handle
        ? "single"
        : "bundle";

    // === Dropdown functions (matching product-variant-size-dropdown.liquid) ===

    function getSizeSelect() {
      return document.getElementById(selectId);
    }

    function getDropdown() {
      return document.getElementById(dropdownId);
    }

    function getOptionsContainer() {
      return document.getElementById(optionsId);
    }

    const mobileSizeQuery = window.matchMedia("(max-width: 991px)");
    function formatSizeLabel(size) {
      const label = String(size || "").replace(/:$/, "").trim();
      if (mobileSizeQuery.matches) return label;
      return label + ":";
    }
    function splitSizeParts(size) {
      const label = String(size || "").replace(/:$/, "").trim();
      const parts = label.split("|");
      if (!mobileSizeQuery.matches || parts.length < 2) return null;
      return {
        name: parts[0].trim(),
        detail: parts.slice(1).join("|").trim(),
      };
    }
    function applySizeCaption(el, size) {
      if (!el) return;
      const split = splitSizeParts(size);
      if (!split) {
        el.textContent = formatSizeLabel(size);
        return;
      }
      el.textContent = "";
      const nameEl = document.createElement("span");
      nameEl.textContent = split.name + " |";
      const detailEl = document.createElement("span");
      detailEl.className = "_1_caption_part--detail";
      detailEl.textContent = split.detail;
      el.append(nameEl, detailEl);
    }
    function sizeCaptionHtml(size) {
      const split = splitSizeParts(size);
      if (!split) return formatSizeLabel(size).replace(/:$/, "");
      const escapeHtml = (value) =>
        String(value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
      return `${escapeHtml(split.name + " |")}<span class="_1_caption_part--detail">${escapeHtml(split.detail)}</span>`;
    }

    function updateCustomDropdownDisplay() {
      const selectEl = getSizeSelect();
      const dropdown = getDropdown();
      if (!selectEl || !dropdown) return;

      const selectedOption = selectEl.options[selectEl.selectedIndex];
      if (!selectedOption || selectedOption.disabled) return;

      const size =
        selectedOption.getAttribute("data-size") ||
        selectedOption.textContent ||
        "";
      const salePrice = selectedOption.getAttribute("data-price") || "";
      const regularPrice =
        selectedOption.getAttribute("data-regular-price") || "";
      const showRegularPrice = regularPrice && regularPrice !== salePrice;

      const sizeEl = dropdown.querySelector("._1_caption");
      applySizeCaption(sizeEl, size);

      const hidePrices = mobileSizeQuery.matches;
      dropdown.querySelectorAll("._1_pp_drd_price_wrp").forEach(function (wrapper) {
        wrapper.style.display = hidePrices ? "none" : "";
      });

      const salePriceEl = dropdown.querySelector("._1_pp_drd_price");
      if (salePriceEl && !hidePrices) salePriceEl.textContent = salePrice;

      const regularPriceWrapper = dropdown.querySelector(
        "._1_pp_drd_price_wrp--rrp"
      );
      if (regularPriceWrapper && !hidePrices) {
        if (showRegularPrice) {
          regularPriceWrapper.style.display = "flex";
          const regularPriceEl =
            regularPriceWrapper.querySelector(".pp_price_drd_rrp");
          if (regularPriceEl) regularPriceEl.textContent = regularPrice;
        } else {
          regularPriceWrapper.style.display = "none";
        }
      }
    }

    function getSelectedVariantId() {
      const selectEl = getSizeSelect();
      if (!selectEl) return "";

      const selectedOption = selectEl.options[selectEl.selectedIndex];
      if (!selectedOption || selectedOption.disabled) return "";

      return selectedOption.value || "";
    }

    function syncSelectedVariantToProductForms() {
      const selectedVariantId = getSelectedVariantId();
      if (!selectedVariantId) return;

      document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
        if (
          form.closest("._1_pp_upsell_wrp") ||
          form.querySelector('input[name="upsell_id"]')
        ) {
          return;
        }

        let variantInput = form.querySelector('input[name="id"]');
        if (!variantInput) {
          variantInput = document.createElement("input");
          variantInput.type = "hidden";
          variantInput.name = "id";
          form.appendChild(variantInput);
        }

        variantInput.disabled = false;
        variantInput.value = selectedVariantId;
        variantInput.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    function generateDropdownOptions() {
      const selectEl = getSizeSelect();
      const optionsContainer = getOptionsContainer();
      if (!selectEl || !optionsContainer) return;

      optionsContainer.innerHTML = "";

      const expandContainer = document.createElement("div");
      expandContainer.className = "_1_pp_drd_expand";

      Array.from(selectEl.options).forEach(function (option, index) {
        if (index === 0) return; // Skip placeholder
        if (option.disabled) return;

        const size =
          option.getAttribute("data-size") || option.textContent || "";
        const salePrice = option.getAttribute("data-price") || "";
        const regularPrice = option.getAttribute("data-regular-price") || "";
        const showRegularPrice = regularPrice && regularPrice !== salePrice;

        const optionWrapper = document.createElement("div");
        optionWrapper.className = "_1_pp_drd_option_wrp";
        optionWrapper.setAttribute("data-value", option.value);

        const priceMarkup = mobileSizeQuery.matches
          ? ""
          : `
          <div class="_1_pp_drd_exp_prices_wrp">
            <div class="_1_pp_drd_price_wrp">
              <div class="_1_pp_drd_price">${salePrice}</div>
            </div>
            ${
              showRegularPrice
                ? `
            <div class="_1_pp_drd_price_wrp">
              <div class="_1_price_line"></div>
              <div class="pp_price_drd_rrp">${regularPrice}</div>
            </div>
            `
                : ""
            }
          </div>
        `;

        optionWrapper.innerHTML = `
          <div class="_1_pp_drd_size">
            <div class="_1_caption bold">${sizeCaptionHtml(size)}</div>
          </div>
          ${priceMarkup}
        `;

        optionWrapper.addEventListener("click", function () {
          const value = this.getAttribute("data-value");
          selectEl.value = value;
          const changeEvent = new Event("change", { bubbles: true });
          selectEl.dispatchEvent(changeEvent);
          updateCustomDropdownDisplay();

          const optionsContainerInner = getOptionsContainer();
          if (optionsContainerInner)
            optionsContainerInner.style.display = "none";

          const dropdownEl = getDropdown();
          if (dropdownEl) {
            const arrowImg = dropdownEl.querySelector("._1_arr_drd");
            if (arrowImg) arrowImg.style.transform = "rotate(0deg)";
          }
        });

        expandContainer.appendChild(optionWrapper);
      });

      optionsContainer.appendChild(expandContainer);
    }

    function initCustomDropdown() {
      const dropdown = getDropdown();
      const optionsContainer = getOptionsContainer();
      const selectEl = getSizeSelect();
      if (!dropdown || !optionsContainer || !selectEl) return;

      updateCustomDropdownDisplay();
      syncSelectedVariantToProductForms();

      selectEl.addEventListener("change", function () {
        syncSelectedVariantToProductForms();
        updateAddToCartButton();
      });

      dropdown.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();

        const isHidden =
          optionsContainer.style.display === "none" ||
          optionsContainer.style.display === "";
        const arrowImg = dropdown.querySelector("._1_arr_drd");

        if (isHidden) {
          generateDropdownOptions();
          optionsContainer.style.display = "block";
          if (arrowImg) arrowImg.style.transform = "rotate(180deg)";
        } else {
          optionsContainer.style.display = "none";
          if (arrowImg) arrowImg.style.transform = "rotate(0deg)";
        }
      });

      document.addEventListener("click", function (event) {
        const dropdownEl = getDropdown();
        const optionsEl = getOptionsContainer();
        if (!dropdownEl || !optionsEl) return;
        if (
          !dropdownEl.contains(event.target) &&
          !optionsEl.contains(event.target)
        ) {
          optionsEl.style.display = "none";
          const arrowImg = dropdownEl.querySelector("._1_arr_drd");
          if (arrowImg) arrowImg.style.transform = "rotate(0deg)";
        }
      });
    }

    // === Option switching functions ===

    function extractSize(variantTitle, isBundle) {
      if (!variantTitle) return "";
      if (isBundle) {
        const parts = variantTitle.split(" / ");
        return parts[0] || variantTitle;
      }
      return variantTitle;
    }

    function formatMoney(cents) {
      const price = cents / 100;
      return price % 1 === 0 ? "$" + price : "$" + price.toFixed(2);
    }

    function rebuildSelectOptions(productType) {
      const selectEl = getSizeSelect();
      if (!selectEl) return;

      const productData = pickerData[productType];
      if (!productData || !productData.variants || !productData.variants.length) return;

      // Store current selection
      const currentSize =
        selectEl.options[selectEl.selectedIndex]?.getAttribute("data-size");

      // Clear and rebuild
      selectEl.innerHTML = "<option disabled>Select size</option>";

      let firstMatchingIndex = 1;
      productData.variants.forEach((variant, index) => {
        if (!variant.available) return;

        const size = extractSize(variant.title, productType === "bundle");
        const option = document.createElement("option");
        option.value = variant.id;
        option.setAttribute("data-size", size);
        option.setAttribute("data-price", formatMoney(variant.price));
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          option.setAttribute(
            "data-regular-price",
            formatMoney(variant.compare_at_price)
          );
        }
        option.textContent = size;

        // Try to match previous size
        if (size === currentSize) {
          option.selected = true;
        }

        selectEl.appendChild(option);
      });

      // If no match, select first available
      if (selectEl.selectedIndex === 0 && selectEl.options.length > 1) {
        selectEl.selectedIndex = 1;
      }

      // Hide/Show Size Section based on available options
      const dropdown = getDropdown();
      const sizeSections = picker.querySelectorAll(
        ".js-foundation-size-header"
      );
      const hasOptions = selectEl.options.length > 1;

      sizeSections.forEach((section) => {
        section.style.display = hasOptions ? "" : "none";
      });

      if (dropdown) {
        dropdown.style.display = hasOptions ? "" : "none";
      }

      // Show/Hide price section based on available options
      const priceSection = document.getElementById(
        "product-prices-splited-all"
      );
      if (priceSection) {
        priceSection.style.display = hasOptions ? "" : "none";
      }

      updateCustomDropdownDisplay();
      syncSelectedVariantToProductForms();
      updateAddToCartButton();
    }

    function updateAddToCartButton() {
      const selectEl = getSizeSelect();
      const atcButton = document.querySelector('button[name="add"]');
      if (!selectEl || !atcButton) return;

      const selectedOption = selectEl.options[selectEl.selectedIndex];
      // If no valid option is selected (or only placeholder), consider it unavailable
      const isAvailable =
        selectedOption &&
        !selectedOption.disabled &&
        selectEl.options.length > 1;

      const label = atcButton.querySelector("._1_btn_ttl");
      if (!label) {
        // Fallback for simple buttons
        atcButton.textContent = isAvailable ? "ADD TO CART" : "OUT OF STOCK";
      } else {
        label.textContent = isAvailable ? "ADD TO CART" : "OUT OF STOCK";
      }

      if (isAvailable) {
        atcButton.removeAttribute("disabled");
        atcButton.classList.remove(
          "disabled",
          "disactivated",
          "_1_btn_atc-desac"
        );
        const atcPrice = document.querySelector("#product-price-all");
        if (atcPrice) {
          atcPrice.textContent = selectedOption.getAttribute("data-price");
        }
      } else {
        atcButton.setAttribute("disabled", "disabled");
        atcButton.classList.add("disabled", "disactivated", "_1_btn_atc-desac");
      }
    }

    function getSubmitQuantity(form) {
      const linkedQuantityInput = form?.id
        ? document.querySelector(`input[name="quantity"][form="${form.id}"]`)
        : null;
      const inlineQuantityInput = form?.querySelector('input[name="quantity"]');
      const rawQuantity = (linkedQuantityInput || inlineQuantityInput)?.value;
      const parsedQuantity = parseInt(rawQuantity, 10);

      return Number.isInteger(parsedQuantity) && parsedQuantity > 0
        ? parsedQuantity
        : 1;
    }

    function setButtonLoading(atcButton, isLoading) {
      if (!atcButton) return;

      atcButton.toggleAttribute("aria-disabled", isLoading);
      atcButton.classList.toggle("loading", isLoading);

      const spinner = atcButton.querySelector(".loading__spinner");
      if (spinner) {
        spinner.classList.toggle("hidden", !isLoading);
      }
    }

    async function addFoundationSelectionToCart(atcButton) {
      const selectedVariantId = getSelectedVariantId();
      if (!selectedVariantId) return;

      const form = atcButton.closest("form") || atcButton.form;
      const cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
      const items = [
        {
          id: parseInt(selectedVariantId, 10),
          quantity: getSubmitQuantity(form),
        },
      ];

      if (window.getSelectedUpsellVariantIds) {
        window.getSelectedUpsellVariantIds().forEach((id) => {
          items.push({ id: parseInt(id, 10), quantity: 1 });
        });
      }

      setButtonLoading(atcButton, true);

      try {
        const sectionsToRender = cart
          ? cart.getSectionsToRender().map((section) => section.id)
          : [];
        const response = await fetch(
          `${window.Shopify.routes.root}cart/add.js`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              items,
              sections: sectionsToRender,
              sections_url: window.location.pathname,
            }),
          }
        );
        const data = await response.json();

        if (data.status) {
          console.error("Foundation Picker: Add to cart error", data);
          return;
        }

        if (window.resetAllUpsellSelections) {
          window.resetAllUpsellSelections();
        }

        if (!cart) {
          window.location = window.Shopify.routes.root + "cart";
          return;
        }

        if (cart.setActiveElement) {
          cart.setActiveElement(document.activeElement);
        }

        try {
          const cartResponse = await fetch(
            `${window.Shopify.routes.root}cart?section_id=cart-drawer`
          );
          const cartHtml = await cartResponse.text();
          const cartDoc = new DOMParser().parseFromString(cartHtml, "text/html");
          const cartDrawerContent = cartDoc.querySelector("#CartDrawer");
          const targetDrawer = document.querySelector("#CartDrawer");

          if (targetDrawer && cartDrawerContent) {
            targetDrawer.innerHTML = cartDrawerContent.innerHTML;
          }

          if (cart.classList.contains("is-empty")) {
            cart.classList.remove("is-empty");
          }

          const bubbleResponse = await fetch(
            `${window.Shopify.routes.root}cart?section_id=cart-icon-bubble`
          );
          const bubbleHtml = await bubbleResponse.text();
          const bubbleDoc = new DOMParser().parseFromString(
            bubbleHtml,
            "text/html"
          );
          const bubbleContent = bubbleDoc.querySelector(".shopify-section");
          const bubbleTarget = document.getElementById("cart-icon-bubble");

          if (bubbleTarget && bubbleContent) {
            bubbleTarget.innerHTML = bubbleContent.innerHTML;
          }

          if (cart.open) cart.open();
        } catch (cartError) {
          console.error("Foundation Picker: Cart drawer update failed", cartError);
          if (cart.open) cart.open();
        }

        if (
          typeof publish !== "undefined" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: selectedVariantId,
            cartData: data,
          });
        }
      } catch (error) {
        console.error("Foundation Picker: Add to cart failed", error);
      } finally {
        setButtonLoading(atcButton, false);
      }
    }

    function handleOptionClick(e) {
      const button = e.currentTarget;
      const option = button.getAttribute("data-option");

      if (option === currentOption) return;

      // Update UI
      optionButtons.forEach((btn) => {
        btn.classList.remove("active");
        const check = btn.querySelector("._1_mod_sel_check");
        if (check) check.classList.remove("active");
      });

      button.classList.add("active");
      const check = button.querySelector("._1_mod_sel_check");
      if (check) check.classList.add("active");

      currentOption = option;

      // Rebuild dropdown with new product's variants
      rebuildSelectOptions(option);

      // Store current selection for form submission
      picker.setAttribute("data-current-option", option);

      // Dispatch custom event
      document.dispatchEvent(
        new CustomEvent("foundation-option:changed", {
          detail: {
            option: option,
            productData: pickerData[option],
          },
        })
      );
    }

    // === Add to Cart interceptor ===

    function setupAddToCartInterceptor() {
      document.addEventListener(
        "click",
        function (e) {
          const atcButton = e.target.closest(
            'button[name="add"], .product-form__submit'
          );
          if (!atcButton) return;

          const currentOptionAttr =
            picker.getAttribute("data-current-option") || currentOption;
          const productData = pickerData[currentOptionAttr];
          if (!productData) return;

          const selectedVariantId = getSelectedVariantId();

          if (!selectedVariantId) return;

          syncSelectedVariantToProductForms();
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          addFoundationSelectionToCart(atcButton);
          console.log("Foundation Picker: Set variant ID to", selectedVariantId);
        },
        true
      );

      document.addEventListener(
        "submit",
        function (e) {
          const form = e.target.closest('form[action*="/cart/add"]');
          if (!form) return;

          syncSelectedVariantToProductForms();
        },
        true
      );
    }

    // === Size Guide Scroll ===

    function initSizeGuideScroll() {
      const scrollLinks = picker.querySelectorAll(".js-scroll-to-id");
      scrollLinks.forEach((link) => {
        link.addEventListener("click", function (e) {
          e.preventDefault();
          const targetId = this.getAttribute("data-target-id");
          if (!targetId) return;

          const targetSection = document.getElementById(targetId);
          if (targetSection) {
            targetSection.scrollIntoView({ behavior: "smooth" });
          } else {
            console.warn(
              "Foundation Picker: Scroll target not found:",
              targetId
            );
          }
        });
      });
    }

    // === Initialize ===

    optionButtons.forEach((btn) => {
      btn.addEventListener("click", handleOptionClick);
    });

    initCustomDropdown();
    setupAddToCartInterceptor();
    initSizeGuideScroll();

    // Set initial state
    rebuildSelectOptions(currentOption);
    picker.setAttribute("data-current-option", currentOption);

    console.log(
      "Foundation Option Picker initialized with option:",
      currentOption
    );
  }

  // Run on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFoundationOptionPicker);
  } else {
    initFoundationOptionPicker();
  }
})();
